import { memoize } from 'lodash';
// @ts-ignore
import type * as RCL from 'rclnodejs';

import { makeLogNamespace } from '@sb/log/log';
import { loadHostIdentity } from '@sb/service-interfaces/host/identity';

import { getNode } from './getNode';
import { getRCL } from './getRCL';

const log = makeLogNamespace('speedScaleFactor');

type PublishedScalingState = {
  factor: number;
  stopTime: number;
};

const DEFAULT_PUBLISHED_STATE: PublishedScalingState = {
  factor: 1.0,
  stopTime: Number.NaN,
};

let lastPublishedState: PublishedScalingState = { ...DEFAULT_PUBLISHED_STATE };

export class SpeedScaleFactorPublisher {
  private speedScaleFactor: number = 1.0;

  private stoppingTime: number | null = null;

  private publishInterval: ReturnType<typeof setInterval> | undefined;

  private publishIntervalMS = 1000;

  private otherScaleFactorsGetter: () => [speedRestriction: number];

  private publisher: RCL.Publisher<'std_msgs/msg/Float64MultiArray'>;

  public constructor(
    publisher: RCL.Publisher<'std_msgs/msg/Float64MultiArray'>,
  ) {
    this.publisher = publisher;
    this.otherScaleFactorsGetter = () => [1.0];
  }

  public setOtherScaleFactorsGetter(getter: () => [speedRestriction: number]) {
    this.otherScaleFactorsGetter = getter;
  }

  public startPublishingSpeedScaleFactor() {
    if (this.publishInterval) {
      log.info('startPublishingSpeedScaleFactor', 'already publishing');

      return;
    }

    log.info('startPublishingSpeedScaleFactor', 'starting to publish');

    this.publishInterval = setInterval(async () => {
      await this.publishSpeedScaleFactor();
    }, this.publishIntervalMS);
  }

  public stopPublishingSpeedScaleFactor() {
    log.info('stopPublishingSpeedScaleFactor', 'stopping to publish');

    if (this.publishInterval) {
      clearInterval(this.publishInterval);
      this.publishInterval = undefined;
    }
  }

  public async setSpeedScaleFactor(
    speedScaleFactor: number,
    stopTimeSeconds?: number,
  ) {
    log.info(
      'setSpeedScaleFactor',
      `setting speed scale factor: ${speedScaleFactor}`,
    );

    this.speedScaleFactor = speedScaleFactor;
    this.stoppingTime = stopTimeSeconds ?? null;
    await this.publishSpeedScaleFactor();
  }

  /**
   * Get the current speed scale factor value
   */
  public getSpeedScaleFactor(): number {
    return this.speedScaleFactor;
  }

  private async publishSpeedScaleFactor() {
    const rcl = await getRCL();

    const factors = [this.speedScaleFactor];

    factors.push(...this.otherScaleFactorsGetter());

    const message = rcl.createMessageObject('std_msgs/msg/Float64MultiArray');

    const newSpeedScaleFactor = Math.min(...factors);

    const stopTime = this.stoppingTime ?? Number.NaN;

    if (
      newSpeedScaleFactor !== lastPublishedState.factor ||
      // need to check for NaN since NaN == Nan is always false
      (stopTime !== lastPublishedState.stopTime &&
        !(Number.isNaN(stopTime) && Number.isNaN(lastPublishedState.stopTime)))
    ) {
      log.info(
        'publishSpeedScaleFactor',
        `Publishing scaling factor ${newSpeedScaleFactor} with stop time ${Number.isNaN(stopTime) ? 'auto' : stopTime}`,
      );
    }

    lastPublishedState = { factor: newSpeedScaleFactor, stopTime };

    const payload = [newSpeedScaleFactor];

    if (!Number.isNaN(stopTime)) {
      payload.push(stopTime);
    }

    message.layout = { dim: [], data_offset: 0 };
    message.data = payload;

    this.publisher.publish(message);
  }
}

export const getSpeedScaleFactorPublisher = memoize(async () => {
  const node = await getNode();

  const robotID = (await loadHostIdentity())?.robotId ?? 'local';
  const topic = `/${robotID}/ro1/hardware/scaling_factor`;

  log.info('getSpeedScaleFactorPublisher', `publishing to ${topic}`);

  const publisher = node.createPublisher(
    'std_msgs/msg/Float64MultiArray',
    topic,
  );

  const speedScaleFactorPublisher = new SpeedScaleFactorPublisher(publisher);

  return speedScaleFactorPublisher;
});
